import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Building2, Plane, Bus, BadgeCheck, UtensilsCrossed, Ticket, ShoppingBag, Route, Compass,
  BedDouble, CarFront, MapPin, ChevronRight, Check,
} from "lucide-react";
import SEO from "../lib/seo";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import PackageCard from "../components/PackageCard";
import FaqAccordion from "../components/FaqAccordion";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { getDestination, getPackages, getFaqs } from "../lib/api";
import { useLead, useSettings } from "../lib/store";
import { track } from "../lib/track";
import { waLink, MSG } from "../lib/whatsapp";
import { WhatsAppIcon, FlagChip } from "../components/icons";

const SERVICE_ICONS = {
  Hotel: Building2,
  "Hotel Accommodation": Building2,
  "Airport Transfer": Plane,
  "Private Bus": Bus,
  "Private Van/Bus": Bus,
  "Private Transportation": Bus,
  "Local Guide": BadgeCheck,
  "Halal Restaurant": UtensilsCrossed,
  "Attraction Ticket": Ticket,
  "Attraction Tickets": Ticket,
  "Shopping Tour": ShoppingBag,
  "Custom Itinerary": Route,
};
const serviceIcon = (s) => SERVICE_ICONS[s] || Compass;

export default function DestinationDetail() {
  const { slug } = useParams();
  const [dest, setDest] = useState(null);
  const [packages, setPackages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [failed, setFailed] = useState(false);
  const { openLead } = useLead();
  const { settings } = useSettings();

  useEffect(() => {
    setDest(null);
    setFailed(false);
    getDestination(slug)
      .then((d) => {
        setDest(d);
        track("view_destination", { destination: slug });
      })
      .catch(() => setFailed(true));
    getPackages({ destination: slug }).then(setPackages).catch(() => {});
    getFaqs().then((f) => setFaqs(f.slice(0, 4))).catch(() => {});
  }, [slug]);

  if (failed) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-6 pt-24 text-center">
        <p className="eyebrow">404</p>
        <h1 className="font-heading text-3xl font-bold text-charcoal">Destinasi tidak ditemukan</h1>
        <Link className="btn btn-primary" to="/destinations">Lihat Semua Destinasi</Link>
      </div>
    );
  }

  if (!dest) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-24">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-tosca-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`Land Arrangement ${dest.name} | Beyond Travel`}
        description={dest.short_desc}
        image={dest.hero_image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TouristDestination",
          name: `${dest.name} Land Arrangement`,
          description: dest.short_desc,
          url: window.location.href,
        }}
      />

      <PageHero
        image={dest.hero_image}
        eyebrow={`Destinasi ${dest.region}`}
        title={`${dest.name} Land Arrangement`}
        sub={dest.short_desc}
        crumb={`${dest.name}`}
      >
        <button
          onClick={() => {
            track("request_quotation", { destination: slug });
            openLead({ destination: dest.name, source: `destination:${slug}` });
          }}
          className="btn btn-gold !px-8 !py-3.5"
          data-testid={`destination-request-quotation-${slug}`}
        >
          Request Quotation
        </button>
        <a
          href={waLink(settings, MSG.destination(dest.name))}
          target="_blank"
          rel="noreferrer"
          onClick={() => track("click_whatsapp", { destination: slug, source: "destination_hero" })}
          className="btn btn-wa !px-8 !py-3.5"
          data-testid={`destination-whatsapp-${slug}`}
        >
          <WhatsAppIcon className="h-5 w-5" /> Konsultasi {dest.name}
        </a>
      </PageHero>

      <section className="section bg-white">
        <div className="container-x grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="Destination Overview" title={`Mengapa ${dest.name}?`} />
            <Reveal delay={0.15}>
              <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">{dest.overview}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-6 flex flex-wrap gap-2">
                {dest.services.map((s) => (
                  <span key={s} className="chip bg-tosca-50 text-tosca-700">{s}</span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="space-y-4">
            {dest.why_choose.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1}>
                <div className="flex gap-4 rounded-2xl border border-slate-100 bg-cream p-5 card-hover" data-testid={`why-choose-${slug}-${i + 1}`}>
                  <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-tosca-600 font-heading text-sm font-black text-gold">
                    0{i + 1}
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-charcoal">{w.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-500">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-cream">
        <div className="container-x">
          <SectionHeading eyebrow="Popular Cities" title={`Kota Populer di ${dest.name}`} />
          <div className="mt-8 flex flex-wrap gap-2.5">
            {dest.cities.map((c, i) => (
              <Reveal key={c} delay={Math.min(i * 0.05, 0.3)}>
                <span className="chip cursor-default border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-charcoal shadow-crystal transition hover:border-tosca-600 hover:text-tosca-700">
                  <MapPin className="h-4 w-4 text-tosca-600" /> {c}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x">
          <SectionHeading eyebrow="Popular Attractions" title="Objek Wisata Populer" />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {dest.attractions.map((a, i) => (
              <Reveal key={a.name} delay={Math.min(i * 0.06, 0.3)} className="h-full">
                <div className="h-full rounded-3xl border border-slate-100 bg-cream p-6 card-hover" data-testid={`attraction-${slug}-${i + 1}`}>
                  <FlagChip code={dest.flag} className="!bg-tosca-600 !ring-0" />
                  <h3 className="mt-4 font-heading text-base font-bold text-charcoal">{a.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {dest.gallery?.length > 0 && (
        <section className="section bg-white pt-0" data-testid={`gallery-${slug}`}>
          <div className="container-x">
            <SectionHeading eyebrow="Gallery" title={`Galeri ${dest.name}`} sub="Cuplikan suasana perjalanan di destinasi ini." />
            <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-2">
              {dest.gallery.map((img, i) => (
                <Reveal key={img} delay={Math.min(i * 0.08, 0.3)}>
                  <div className="group overflow-hidden rounded-3xl shadow-crystal">
                    <img
                      src={img}
                      alt={`${dest.name} gallery ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section bg-tosca-900">
        <div className="container-x">
          <SectionHeading eyebrow="Popular Services" title="Layanan Land Arrangement" sub="Semua layanan di bawah dapat dikombinasikan sesuai paket dan kebutuhan Anda." dark />
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {dest.services.map((s, i) => {
              const Icon = serviceIcon(s);
              return (
                <Reveal key={s} delay={Math.min(i * 0.05, 0.3)}>
                  <div className="flex h-full items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:border-gold/50">
                    <Icon className="h-5 w-5 flex-shrink-0 text-gold" />
                    <span className="text-sm font-semibold text-white">{s}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {packages.length > 0 && (
        <section className="section bg-cream">
          <div className="container-x">
            <SectionHeading eyebrow="Sample Packages" title={`Paket Land Arrangement ${dest.name}`} />
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {packages.map((p, i) => (
                <PackageCard key={p.slug} pkg={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section bg-white">
        <div className="container-x grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="Sample Itinerary" title="Contoh Itinerary" />
            <Reveal delay={0.15}>
              <p className="mt-4 text-sm italic text-slate-400">Itinerary dapat disesuaikan dengan kebutuhan customer.</p>
            </Reveal>
            <div className="mt-8 space-y-0">
              {dest.sample_itinerary.map((day, i) => (
                <Reveal key={day.day} delay={Math.min(i * 0.08, 0.3)}>
                  <div className="relative flex gap-5 pb-8 last:pb-0" data-testid={`itinerary-${slug}-day-${day.day}`}>
                    {i < dest.sample_itinerary.length - 1 && (
                      <span className="absolute left-[27px] top-14 h-[calc(100%-3.5rem)] w-px bg-gradient-to-b from-gold to-tosca-200" />
                    )}
                    <span className="z-10 inline-flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-tosca-600 font-heading text-sm font-black text-white ring-4 ring-gold/30">
                      D{day.day}
                    </span>
                    <div className="rounded-2xl border border-slate-100 bg-cream p-5">
                      <h3 className="font-heading text-base font-bold text-charcoal">{day.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{day.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {[
              { icon: BedDouble, title: "Hotel Options", items: dest.hotel_options },
              { icon: CarFront, title: "Transportation Options", items: dest.transport_options },
              { icon: UtensilsCrossed, title: "Halal Food Options", items: dest.halal_options },
            ].map((block) => (
              <Reveal key={block.title}>
                <div className="rounded-3xl border border-slate-100 bg-cream p-6" data-testid={`${block.title.toLowerCase().replace(/ /g, "-")}-${slug}`}>
                  <h3 className="flex items-center gap-2 font-heading text-base font-bold text-charcoal">
                    <block.icon className="h-5 w-5 text-tosca-600" /> {block.title}
                  </h3>
                  <ul className="mt-4 space-y-2.5">
                    {block.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-slate-500">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {(dest.faqs.length > 0 || faqs.length > 0) && (
        <section className="section bg-cream">
          <div className="container-x max-w-4xl">
            <SectionHeading eyebrow="FAQ" title="Pertanyaan Seputar Destinasi Ini" align="center" />
            <div className="mt-8">
              <FaqAccordion items={[...dest.faqs, ...faqs]} />
            </div>
          </div>
        </section>
      )}

      <CtaSection
        title={`Ready to Explore ${dest.name}?`}
        sub={`Dapatkan quotation Land Arrangement ${dest.name} sesuai kebutuhan perjalanan Anda. Hubungi kami untuk harga terbaru.`}
        testid={`cta-${slug}`}
      />
    </>
  );
}
